<script setup>
import { computed, ref } from 'vue';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Scatter } from 'vue-chartjs';
import { useCompetitorAppConfig } from 'src/composables/useCompetitorAppConfig';
import { useChartDprResize } from 'src/composables/useChartDprResize';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

const props = defineProps({
  applicationType: {
    type: String,
    required: true,
  },
  values: {
    type: Object,
    required: true,
  },
  color: {
    type: String,
    required: false,
    default: '#7a7a7a',
  },
  angle: {
    type: String,
    required: true,
  }
});

const valueForAngleAvailable = computed(() => {
  const aVal = props.values.a.find((row) => row.code === props.angle)?.value;
  const bVal = props.values.b.find((row) => row.code === props.angle)?.value;

  return aVal !== null && bVal !== null;
});

const valuesForAngle = computed(() => ({
  x: props.values.a.find((row) => row.code === props.angle)?.value,
  y: props.values.b.find((row) => row.code === props.angle)?.value,
}));

const data = computed(() => ({
  datasets: [
    {
      data: [valuesForAngle.value],
      fill: false,
      borderColor: props.color,
      backgroundColor: props.color,
      pointRadius: 6,
      pointHoverRadius: 9,
      pointHitRadius: 24,
    }
  ]
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'linear',
      min: -120,
      max: 120,
      ticks: {
        stepSize: 20,
      },
      grid: {
        drawTicks: true,
        color: (ctx) => (ctx.tick.value === 0 ? '#000' : '#ccc'),
      },
      border: {
        display: false,
      },
      title: {
        display: true,
        text: 'a*',
      },
    },
    y: {
      type: 'linear',
      min: -120,
      max: 120,
      ticks: {
        stepSize: 20,
      },
      grid: {
        drawTicks: true,
        color: (ctx) => (ctx.tick.value === 0 ? '#000' : '#ccc'),
      },
      border: {
        display: false,
      },
      title: {
        display: true,
        text: 'b*',
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
  }
}));

const { getApplicationType } = useCompetitorAppConfig();

const applicationTypeConfig = computed(() => getApplicationType(props.applicationType));

const chartRef = ref(null);

useChartDprResize(chartRef);
</script>

<template>
  <div
    v-if="valueForAngleAvailable"
    class="ce-technicalPropertyCard">
    <div
      class="ce-cielab-chart">
      <div class="applicationType">
        {{ applicationTypeConfig.title }}
      </div>

      <div>
        <Scatter
          ref="chartRef"
          :data="data"
          :options="chartOptions" />
      </div>
    </div>
  </div>
</template>
